import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faFileExcel,
  faPaperPlane,
} from "@fortawesome/free-solid-svg-icons";
import {
  invitationService,
  SendInvitationsResponse,
} from "../../services/invitationService";

interface UploadInvitationFileDialogProps {
  open: boolean;
  onClose: () => void;
  courseId: string;
}

interface UploadResult {
  invited: number;
  skipped: number;
  errorDetails: { email: string; error: string }[];
  numProcessed: number;
  errors: number;
  failedInvitationEmails: string[];
}

const UploadInvitationFileDialog: React.FC<UploadInvitationFileDialogProps> = ({
  open,
  onClose,
  courseId,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const steps = ["Select File", "Upload and Process", "Review Results"];

  // Reset the dialog state when it's closed
  useEffect(() => {
    if (!open) {
      resetDialog();
    }
  }, [open]);

  const resetDialog = () => {
    setFile(null);
    setIsUploading(false);
    setIsSending(false);
    setUploadResult(null);
    setActiveStep(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClose = () => {
    onClose();
    resetDialog();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
      setActiveStep(1);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setIsUploading(true);
    try {
      const result: UploadResult =
        (await invitationService.uploadInvitationsExcel(
          courseId,
          file
        )) as unknown as UploadResult;
      setUploadResult(result);
      setActiveStep(2);
    } catch (error) {
      console.error("Failed to upload invitations:", error);
      setUploadResult({
        numProcessed: 0,
        errors: 1,
        errorDetails: [
          { email: "", error: "An unexpected error occurred during upload." },
        ],
        failedInvitationEmails: [],
        invited: 0,
        skipped: 0,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleSendInvitations = async () => {
    setIsSending(true);
    try {
      await invitationService.sendCourseInvitations(courseId);
      // You might want to show a success message here
    } catch (error) {
      console.error("Failed to send invitations:", error);
      // Handle error (e.g., show an error message)
    } finally {
      setIsSending(false);
      onClose();
    }
  };

  const renderContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box display="flex" flexDirection="column" alignItems="center">
            <input
              type="file"
              accept=".csv, .xlsx, .xls"
              style={{ display: "none" }}
              ref={fileInputRef}
              onChange={handleFileChange}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<FontAwesomeIcon icon={faFileExcel} />}
              onClick={() => fileInputRef.current?.click()}
            >
              Select Excel File
            </Button>
            {file && <Typography mt={2}>{file.name}</Typography>}
          </Box>
        );
      case 1:
        return (
          <Box display="flex" flexDirection="column" alignItems="center">
            {isUploading ? (
              <>
                <CircularProgress />
                <Typography mt={2}>Processing invitations...</Typography>
              </>
            ) : (
              <Button
                variant="contained"
                color="primary"
                startIcon={<FontAwesomeIcon icon={faUpload} />}
                onClick={handleUpload}
              >
                Upload and Process
              </Button>
            )}
          </Box>
        );
      case 2:
        return uploadResult ? (
          <Box>
            <Typography variant="h6" gutterBottom>
              Upload Results
            </Typography>
            <Typography>
              Total processed: {uploadResult.numProcessed}
            </Typography>
            <Typography>Successfully added: {uploadResult.invited}</Typography>
            <Typography>
              Skipped because already invited: {uploadResult.skipped}
            </Typography>
            <Typography>Failed: {uploadResult.errors}</Typography>
            {uploadResult.errorDetails.length > 0 && (
              <>
                <Typography variant="subtitle1" mt={2}>
                  Errors:
                </Typography>
                <List>
                  {uploadResult.errorDetails.map((error, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={error.email} />
                      <ListItemText secondary={error.error} />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
            {uploadResult.failedInvitationEmails.length > 0 && (
              <>
                <Typography variant="subtitle1" mt={2}>
                  Failed Email Addresses:
                </Typography>
                <List>
                  {uploadResult.failedInvitationEmails.map((email, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={email} />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
            {uploadResult.invited > 0 && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<FontAwesomeIcon icon={faPaperPlane} />}
                onClick={handleSendInvitations}
                disabled={isSending}
                sx={{ mt: 2 }}
              >
                {isSending ? "Sending..." : "Send Invitation Emails"}
              </Button>
            )}
          </Box>
        ) : null;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Upload Invitations</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {renderContent()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadInvitationFileDialog;
